import { Component, computed, inject, signal, OnDestroy, input } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subscription } from 'rxjs';
import { QaiCarouselComponent, QaiIconComponent, QaiCarouselItemDirective, QaiCallCaptionsComponent, QaiCallAudioIndicatorComponent } from '@qai/ui';
import DOMPurify from 'dompurify';

import { ICallDetailModal } from 'src/app/interfaces/icall-detail-modal';

export type ContentBlockType = 'heading' | 'subheading' | 'body' | 'check-item' | 'bullet';

export interface ContentBlock {
  type: ContentBlockType;
  text: string;
}

@Component({
  selector: 'app-call-preamble',
  standalone: true,
  imports: [QaiCarouselComponent, QaiIconComponent, QaiCarouselItemDirective, QaiCallCaptionsComponent, QaiCallAudioIndicatorComponent],
  templateUrl: './call-roleplay-preamble.component.html',
  styleUrls: ['./call-roleplay-preamble.component.scss'],
})
export class CallPreambleComponent implements OnDestroy {
  readonly callDetails = input<ICallDetailModal | null>(null);
  readonly captionsOn = input<boolean>(false);
  readonly captionText = input<string>('');
  readonly isMobile = signal(false);
  readonly avatarVideoPlaying = signal(false);
  readonly avatarVideoErrored = signal(false);
  private readonly breakpointSub: Subscription;
  readonly cardWidthPx = signal(0);

  constructor() {
    const bp = inject(BreakpointObserver);
    this.breakpointSub = bp.observe('(max-width: 768px)').subscribe((state) => {
      this.isMobile.set(state.matches);
    });
  }

  ngOnDestroy(): void {
    this.breakpointSub.unsubscribe();
  }

  readonly parsedTabs = computed(() =>
    [...(this.callDetails()?.callDetails ?? [])]
      .filter((tab) => tab.tabTitle?.trim() && tab.tabText?.trim())
      .sort((a, b) => a.tabSequence - b.tabSequence)
      .map((tab) => ({
        ...tab,
        blocks: this.parseTabText(tab.tabText),
      })),
  );

  private parseTabText(text: string): ContentBlock[] {
    return text
      .split('\n')
      .map((line) => line.replace(/\t/g, '').trim())
      .filter((line) => line.length > 0)
      .map((line): ContentBlock => {
        const sanitized = DOMPurify.sanitize(line);
        const headingMatch = sanitized.match(/^\*\*(.+)\*\*$/);
        if (headingMatch) {
          return { type: 'heading', text: headingMatch[1] };
        }
        const subheadingMatch = sanitized.match(/^\*(.+)\*$/);
        if (subheadingMatch) {
          return { type: 'subheading', text: subheadingMatch[1] };
        }
        if (sanitized.startsWith('@@')) {
          return { type: 'check-item', text: sanitized.replace(/^@@\s*/, '') };
        }
        if (/^-\s/.test(sanitized)) {
          return { type: 'bullet', text: sanitized.replace(/^-\s*/, '') };
        }
        return { type: 'body', text: sanitized };
      });
  }
  protected handleAvatarVideoPlaying(): void {
    this.avatarVideoPlaying.set(true);
    this.avatarVideoErrored.set(false);
  }

  protected handleAvatarVideoError(): void {
    this.avatarVideoErrored.set(true);
    this.avatarVideoPlaying.set(false);
  }
}